/**
 * `/inventory/purchase-draft` — draft purchase order (roadmap Phase 3 #2b).
 *
 * The below-reorder-level lines assembled into a proposed S&S order with an estimated cost. The
 * operator adjusts quantities inline, then Create PO persists a QUEUED FSP-N purchase order (the
 * draft itself PROPOSES ONLY — nothing is ordered until the PO is placed in S&S). Cost data is
 * admin/inventory-admin gated server-side (a non-authorized user sees the 403 message). The old
 * Copy/CSV export buttons were removed 2026-08-08 (owner: never used).
 *
 * Read + manual Refresh (no polling): the endpoint fetches S&S cost per line. The estimated total is
 * recomputed client-side from the adjusted quantities × the server-resolved unit cost.
 *
 * UI refresh PR-6 (2026-08, docs/inventory-ui-refresh-plan-v1.md §3.4): the 720px table became one
 * responsive list — see `DraftLines`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FilePlus2, Shirt, SquarePen } from 'lucide-react';
import { useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { invalidateInventory } from './inventory-lib';
import { ManualPoDialog } from './manual-po-dialog';
import {
  type DraftOrderRow,
  draftLabel,
  draftVariant,
  money,
  type PurchaseDraftLine,
  parseOrderQty,
} from './purchase-draft-lib';
import type { PurchaseOrderView } from './purchase-order-lib';
import { ConfirmDialog, PurchaseOrdersPanel } from './purchase-orders-panel';

export function PurchaseDraftPage() {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const [qtyOverrides, setQtyOverrides] = useState<Record<string, string>>({});
  const [createdPo, setCreatedPo] = useState<PurchaseOrderView | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Stable idempotency token for the NEXT create — rotated on success so distinct POs get distinct
  // tokens, but a dropped-response RETRY reuses it (server replays the same PO, never a second).
  const tokenRef = useRef<string>(crypto.randomUUID());
  // A `disabled={isPending}` prop loses the double-click race — React commits the re-render AFTER the
  // second click has already dispatched. A ref is checked synchronously, so it can't be outrun.
  const submittingRef = useRef(false);

  const query = useQuery({
    queryKey: ['inventory-purchase-draft'],
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/purchase-draft');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the draft PO.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const draft = query.data;

  // Effective (operator-adjusted) order qty for a line: the parsed override (blank → 0), else the
  // server suggestion. `null` = an invalid override (negative/decimal/garbage) — the SAME value drives
  // display, line total, and export, so what's shown can never diverge from what's ordered.
  const resolvedQty = (l: PurchaseDraftLine): number | null => {
    const raw = qtyOverrides[l.ssSku];
    return raw === undefined ? l.orderQty : parseOrderQty(raw);
  };

  const invalidCount = (draft?.lines ?? []).filter((l) => resolvedQty(l) === null).length;

  // Count only lines with a valid, positive quantity — a cleared/zeroed line is intentionally dropped.
  const orderRows: DraftOrderRow[] = (draft?.lines ?? []).flatMap((l) => {
    const qty = resolvedQty(l);
    if (qty === null || qty <= 0) return [];
    return [
      {
        ssSku: l.ssSku,
        label: draftLabel(l),
        variant: draftVariant(l),
        onHand: l.onHand,
        orderQty: qty,
        unitCost: l.unitCost,
        lineTotal: l.unitCost === null ? null : l.unitCost * qty,
      },
    ];
  });
  // Client-recomputed estimate from adjusted quantities × the server-resolved unit cost (priced only).
  const estimatedTotal = orderRows.reduce((sum, r) => sum + (r.lineTotal ?? 0), 0);

  // Persist the current (adjusted, valid, positive) draft lines as a QUEUED purchase order. The server
  // re-resolves cost + mints the FSP-N number; the client only sends identity + quantity + the token.
  const createPo = useMutation({
    mutationFn: async () => {
      const lines = (draft?.lines ?? []).flatMap((l) => {
        const qty = resolvedQty(l);
        if (qty === null || qty <= 0) return [];
        return [
          {
            ssSku: l.ssSku,
            brandName: l.brandName,
            styleName: l.styleName,
            colorName: l.colorName,
            sizeName: l.sizeName,
            orderQty: qty,
          },
        ];
      });
      const { data, error } = await client.POST('/purchase-orders', {
        body: { lines, clientToken: tokenRef.current, source: 'reorder' },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to create the purchase order.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: (po) => {
      setCreatedPo(po);
      tokenRef.current = crypto.randomUUID(); // rotate so the next create is a distinct PO
      setQtyOverrides({}); // clear adjustments — the netted draft refetches with the ordered lines gone
      invalidateInventory(qc);
    },
    onSettled: () => {
      submittingRef.current = false;
      setConfirmOpen(false);
    },
  });

  // One click here persists EVERY line at its suggested quantity. Since 2026-08-12 those quantities also
  // rebuild the shelf buffer on top of committed orders, so the basket is several times larger than it
  // used to be — and the PO it creates immediately counts as on-order, which SUPPRESSES the reorder
  // signal that would otherwise re-flag an accidental over-order. The safety net is exactly what the
  // accident switches off, so the confirm goes in front of it.
  const submitPo = () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    createPo.mutate();
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-muted/30 px-3.5 py-2.5 text-xs text-muted-foreground">
        Blanks to buy — enough to cover the open orders AND still leave the shelf at its reorder
        level. Blanks committed to unpressed orders are still sitting on the shelf, so on-hand alone
        overstates what's free. Adjust the quantities, then{' '}
        <span className="font-medium">Create PO</span> — nothing is ordered until you place the PO
        in S&amp;S.
      </div>

      <ManualPoDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={(po) => setCreatedPo(po)}
      />

      {query.isError && !draft && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load the draft PO</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Unknown error. Try refreshing.'}
          </AlertDescription>
        </Alert>
      )}
      {query.isError && draft && (
        <p className="text-sm text-destructive">
          Couldn't refresh — showing the last loaded draft.
        </p>
      )}

      {/* Create-PO result — kept at the page level so it persists after the ordered lines drop off the
          draft (the block below unmounts when the draft empties). */}
      {createPo.error instanceof Error && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't create the purchase order</AlertTitle>
          <AlertDescription>{createPo.error.message}</AlertDescription>
        </Alert>
      )}
      {createdPo && (
        <Alert>
          <AlertTitle className="flex items-center gap-2">
            <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" />
            Purchase order {createdPo.poNumber} created
          </AlertTitle>
          <AlertDescription>
            Its {createdPo.totalUnits} units now count as on the way, so they've left the draft.
            Place it with S&amp;S from the list below.
          </AlertDescription>
        </Alert>
      )}

      {/* One action row, right-aligned (mockup §Purchase draft). Build-a-manual-PO renders whether or
          not there is a draft — an empty draft is exactly when you reach for it — while Create PO only
          appears once there are lines to create from. The invalid-quantity reason sits on this row so
          the explanation for a blocked money button is never further away than the button itself. */}
      {canWrite && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {invalidCount > 0 && (
            <span className="mr-auto text-sm font-medium text-destructive">
              Fix {invalidCount} invalid quantit{invalidCount === 1 ? 'y' : 'ies'} first
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => setManualOpen(true)}>
            <SquarePen className="size-4" />
            Build a manual PO
          </Button>
          {draft && draft.lines.length > 0 && (
            <Button
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={invalidCount > 0 || orderRows.length === 0 || createPo.isPending}
            >
              <FilePlus2 className="size-4" />
              {createPo.isPending ? 'Creating…' : 'Create PO'}
            </Button>
          )}
        </div>
      )}

      {confirmOpen && (
        <ConfirmDialog pending={createPo.isPending} onClose={() => setConfirmOpen(false)}>
          <DialogHeader>
            <DialogTitle>
              Create a purchase order for {orderRows.length} blank
              {orderRows.length === 1 ? '' : 's'}?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2">
                <p>
                  <span className="font-medium text-foreground tabular-nums">
                    {orderRows.reduce((n, r) => n + r.orderQty, 0)} pcs
                  </span>{' '}
                  · about{' '}
                  <span className="font-medium text-foreground">{money(estimatedTotal)}</span>{' '}
                  estimated.
                </p>
                <p>
                  This covers the open orders and rebuilds each blank's shelf level on top of them.
                  It saves a QUEUED PO — nothing is ordered with S&amp;S until you place it.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={createPo.isPending}
            >
              Cancel
            </Button>
            <Button onClick={submitPo} disabled={createPo.isPending}>
              {createPo.isPending ? 'Creating…' : 'Create PO'}
            </Button>
          </DialogFooter>
        </ConfirmDialog>
      )}

      {query.isLoading && <Skeleton className="h-48 w-full" />}

      {draft && draft.lines.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <CheckCircle2 className="size-8 text-emerald-600 dark:text-emerald-400" />
            <p className="font-medium">Nothing to buy</p>
            <p className="text-sm text-muted-foreground">
              Open orders are covered and every blank is at its reorder level. Set a reorder level
              on a blank in On hand to see it here.
            </p>
          </CardContent>
        </Card>
      )}

      {draft && draft.lines.length > 0 && (
        <>
          <DraftLines
            lines={draft.lines}
            qtyOf={(l) => qtyOverrides[l.ssSku] ?? String(l.orderQty)}
            onQty={(ssSku, v) => setQtyOverrides((prev) => ({ ...prev, [ssSku]: v }))}
            resolvedQty={resolvedQty}
          />

          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{orderRows.length}</span> blank
            {orderRows.length === 1 ? '' : 's'} to reorder ·{' '}
            <span className="font-medium text-foreground">{money(estimatedTotal)}</span> estimated
            {draft.pricedCount < draft.lines.length && (
              <>
                {' '}
                (cost for {draft.pricedCount}/{draft.lines.length} lines)
              </>
            )}{' '}
            · costs are estimates — S&amp;S shows the final price at checkout.
          </p>
        </>
      )}

      {canWrite && <PurchaseOrdersPanel />}
    </div>
  );
}

/* Desktop row grid: identity | on hand | buy up to | on order | order qty | unit | line total. */
const DRAFT_GRID =
  'md:grid md:grid-cols-[minmax(0,1fr)_4.5rem_6.5rem_5rem_7rem_5.5rem_6rem] md:items-center md:gap-x-2';

/**
 * The target decomposes into shelf buffer + committed open orders. Returned only when the two actually
 * SUM to the printed target: `buildReorderRow` floors the target at the bare par/ROP, so on the rare row
 * where that floor wins the parts don't add up — and an equation that doesn't add up is worse than none.
 * Null on an older API too, where both fields are simply absent (this is what makes shipping the SPA
 * before the API harmless).
 */
export function targetParts(l: {
  threshold: number;
  bufferTarget?: number;
  committedDemand?: number;
}): { level: number; committed: number } | null {
  const { bufferTarget: level, committedDemand: committed } = l;
  if (level == null || committed == null) return null;
  return level + committed === l.threshold ? { level, committed } : null;
}

/**
 * The draft lines. ONE DOM tree, two layouts (the same trick the On-hand list uses): a grid of
 * columns from `md` up, and on a phone a card per line — identity, one plain sentence carrying what
 * the columns said, and the order-qty field on its own full-width, thumb-height row. The 720px
 * horizontally-scrolling table it replaces put the only editable cell out of reach on a phone.
 */
function DraftLines({
  lines,
  qtyOf,
  onQty,
  resolvedQty,
}: {
  lines: PurchaseDraftLine[];
  qtyOf: (l: PurchaseDraftLine) => string;
  onQty: (ssSku: string, value: string) => void;
  resolvedQty: (l: PurchaseDraftLine) => number | null;
}) {
  return (
    <section className="overflow-hidden rounded-lg border">
      <div
        className={cn(
          DRAFT_GRID,
          'hidden bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground md:grid',
        )}
      >
        <span>Blank</span>
        <span className="text-right">On hand</span>
        <span className="text-right">Buy up to</span>
        <span className="text-right">On order</span>
        <span className="text-right">Order qty</span>
        <span className="text-right">Unit cost</span>
        <span className="text-right">Line total</span>
      </div>
      <ul>
        {lines.map((l) => (
          <DraftRow
            key={l.ssSku}
            line={l}
            raw={qtyOf(l)}
            qty={resolvedQty(l)}
            onQty={(v) => onQty(l.ssSku, v)}
          />
        ))}
      </ul>
    </section>
  );
}

function DraftRow({
  line: l,
  raw,
  qty,
  onQty,
}: {
  line: PurchaseDraftLine;
  raw: string;
  qty: number | null;
  onQty: (value: string) => void;
}) {
  const variant = draftVariant(l);
  // `null` = the operator typed something that is not a whole number. The field goes red and Create PO
  // stays blocked until it's fixed — deliberately NOT auto-corrected: silently turning "12.5" into
  // "125" on a page that decides what gets bought is how you over-order by 10×.
  const invalid = qty === null;
  const lineTotal = invalid || l.unitCost === null ? null : l.unitCost * qty;
  const negative = l.onHand < 0;
  // "40 level + 31 on open orders" — why a blank sitting at its par is still being bought.
  const parts = targetParts(l);

  return (
    <li
      className={cn(
        DRAFT_GRID,
        'flex flex-wrap items-center gap-x-3 gap-y-2 border-t px-3 py-2.5 text-sm',
        invalid ? 'bg-red-50/60 dark:bg-red-950/30' : 'odd:bg-muted/20',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5 md:flex-initial">
        {/* The colorway photo — caps and tees are unmistakable at a glance, style codes are not. */}
        {l.imageUrl ? (
          <img
            src={l.imageUrl}
            alt=""
            loading="lazy"
            className="size-10 shrink-0 rounded-md border bg-background object-cover"
          />
        ) : (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-md border bg-background text-muted-foreground">
            <Shirt className="size-5" />
          </span>
        )}
        <div className="min-w-0">
          <div className="truncate font-medium">{draftLabel(l)}</div>
          {variant && <div className="truncate text-xs text-muted-foreground">{variant}</div>}
          <div className="truncate font-mono text-xs text-muted-foreground">{l.ssSku}</div>
          {/* Phone: the desktop number columns, said in one line. */}
          <div className="text-xs text-muted-foreground md:hidden">
            <span className={cn(negative && 'font-semibold text-red-700 dark:text-red-400')}>
              {l.onHand} on hand
            </span>
            {` · buy up to ${l.threshold}`}
            {parts && ` (${parts.level} level + ${parts.committed} on open orders)`}
            {l.onOrder > 0 && ` · ${l.onOrder} on order`}
          </div>
        </div>
      </div>

      <span
        className={cn(
          'hidden text-right tabular-nums md:block',
          negative && 'font-semibold text-red-700 dark:text-red-400',
        )}
      >
        {l.onHand}
      </span>
      <span className="hidden text-right tabular-nums text-muted-foreground md:block">
        {l.threshold}
        {parts && (
          <span className="block text-[10px] leading-tight">
            {parts.level} + {parts.committed}
          </span>
        )}
      </span>
      <span className="hidden text-right tabular-nums text-muted-foreground md:block">
        {l.onOrder > 0 ? l.onOrder : '—'}
      </span>

      {/* Order qty — full width on its own line on a phone, one grid cell on desktop. */}
      <div className="order-last flex w-full items-center gap-2 md:order-none md:w-auto md:justify-end">
        <span className="text-xs text-muted-foreground md:hidden" aria-hidden="true">
          Order
        </span>
        <Input
          type="text"
          inputMode="numeric"
          enterKeyHint="done"
          autoComplete="off"
          aria-label={`Order quantity for ${l.ssSku}`}
          aria-invalid={invalid}
          className={cn(
            'h-11 flex-1 text-center text-base tabular-nums md:h-9 md:w-24 md:flex-initial md:text-right md:text-sm',
            invalid && 'border-destructive text-destructive focus-visible:ring-destructive',
          )}
          value={raw}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => onQty(e.target.value)}
        />
        <span className="text-xs text-muted-foreground md:hidden">
          {l.unitCost === null ? 'no cost' : `× ${money(l.unitCost)}`}
        </span>
      </div>

      <span className="hidden text-right tabular-nums md:block">
        {l.unitCost === null ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          money(l.unitCost)
        )}
      </span>
      <span className="text-right font-medium tabular-nums">{money(lineTotal)}</span>
    </li>
  );
}
